import {
  IconButton,
  ITextProps,
  Label,
  Modal,
  Text,
  TextField,
} from "@fluentui/react";
import { AxiosResponse } from "axios";
import moment from "moment";
import "moment/locale/de";
import React, { useEffect, useState } from "react";
import { ApiService } from "../../../services/api.service";
import { HelperService } from "../../../services/helper.service";
import {
  cancelIcon,
  contentStyles,
  iconButtonStyles,
  labelStyles,
  valueStyles,
} from "../../Controls/component-styles";
import {
  contextSegments,
  FormMode,
  InputType,
} from "../../Controls/context-segments";
import DynamicDatePicker from "../../Controls/DatePicker/DynamicDatePicker";
import DeleteDialog from "../../Controls/Dialogs/DeleteDialog";
import { DynamicFormCommandBar } from "../../Controls/DynamicFormCommandBar/DynamicFormCommandBar";
import { attributes, GoodInfo, IGoodInfoDocument } from "./good-info.type";

moment.locale("de");

interface IProps {
  isModalOpen: boolean;
  selectedItem: IGoodInfoDocument | null;
  formMode: FormMode;
  fkName?: string;
  closeModal: () => void;
  setFormMode: (formMode: FormMode) => void;
  setReload?: (reload: boolean) => void;
  setCreatedItem?: (e: any, newItem: any) => void;
}

const apiCalls: ApiService = ApiService.getInstance();
const helperService = HelperService.getInstance();
const formName: string = "good-info-form";

const GoodInfoForm = (props: IProps) => {
  const formHeading: string = helperService.getFormHeading(
    contextSegments.goodInfo.singularName,
    props.formMode
  );

  const [payload, setPayload] = useState<IGoodInfoDocument>(new GoodInfo());
  const [hideDeleteDialog, toggleHideDeleteDialog] = useState<boolean>(true);

  useEffect(() => {
    // In case some item was passed to the form
    // Set this item to the payload
    if (props.selectedItem) {
      setPayload(props.selectedItem);
    }
    // In case no item was passed to the form
    // Set default empty payload
    else {
      setPayload(new GoodInfo());
    }

    // Clear the input when closing the modal
    if (!props.isModalOpen) {
      setPayload(new GoodInfo());
    }
  }, [props.selectedItem, props.isModalOpen]);

  const titleId = helperService.makeId(10);

  /**
   * Invoke create action
   * @param e
   */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // In case payload id exist
    // Update the item
    if (payload.id) {
      apiCalls
        .updateGoodInfo(payload, payload.id)
        .then((res: AxiosResponse<any>) => {
          props.setReload && props.setReload(true);

          props.closeModal();
          setPayload(new GoodInfo());
        });
    }
    // In case no payload id exist
    // Create a new item
    else {
      apiCalls.createGoodInfo(payload).then((res: AxiosResponse<any>) => {
        //   In case creating an item through the dropdown context
        if (props.setCreatedItem && props.fkName && res.data.id) {
          const fakeEvent = {
            target: {
              name: props.fkName,
              type: "dropdown",
            },
          };
          const newItem: IGoodInfoDocument = {
            id: res.data["id"],
            commodityNumber: res.data["commodityNumber"],
            approvalNumber: res.data["approvalNumber"],
            referenceNumber: res.data["referenceNumber"],
            issueDate: res.data["issueDate"],
          };
          props.setCreatedItem(fakeEvent, newItem);
        }
        props.setReload && props.setReload(true);

        props.closeModal();
        setPayload(new GoodInfo());
      });
    }
  };

  /**
   * Write newValue to state by element name
   * @param e
   * @param newValue
   */
  const handleOnChange = (e: any, newValue: any) => {
    const { name } = e.target;

    if (newValue === undefined) {
      setPayload({ ...payload, [name]: null });
    } else {
      const inputType: string = e.target.type;
      if (inputType === InputType.text || inputType === InputType.textarea) {
        setPayload({ ...payload, [name]: String(newValue) });
      } else if (inputType === InputType.datepicker) {
        setPayload({ ...payload, [name]: newValue });
      } else if (inputType === InputType.number) {
        setPayload({ ...payload, [name]: Number(newValue) });
      } else if (inputType === InputType.checkbox) {
        setPayload({ ...payload, [name]: Boolean(newValue) });
      } else if (inputType === InputType.dropdown) {
        setPayload({ ...payload, [name]: newValue });
      } else if (inputType === InputType.combobox) {
        setPayload({ ...payload, [name]: newValue.text });
      }
    }
  };
  const isEditable: boolean =
    props.formMode === FormMode.create || props.formMode === FormMode.edit;

  const deleteOpenedItem = async () => {
    toggleHideDeleteDialog(true);
    props.closeModal();
    await apiCalls.deleteGoodInfo(props.selectedItem!.id!);
    props.setReload && props.setReload(true);
  };

  return (
    <Modal
      titleAriaId={titleId}
      isOpen={props.isModalOpen}
      onDismiss={props.closeModal}
      isBlocking={false}
      containerClassName={contentStyles.container}
    >
      <DeleteDialog
        toggleHideDeleteDialog={toggleHideDeleteDialog}
        hideDeleteDialog={hideDeleteDialog}
        selectedItemsCount={1}
        deleteSelectedItems={deleteOpenedItem}
      />
      <div className={contentStyles.header}>
        <Text id={titleId} variant={"xxLarge" as ITextProps["variant"]}>
          {formHeading}
        </Text>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={props.closeModal}
        />
      </div>
      <div className={contentStyles.body}>
        {isEditable && props.isModalOpen ? (
          <form id={formName} onSubmit={onSubmit}>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              closeModal={props.closeModal}
              formName={formName}
            />
            <TextField
              name={attributes.commodityNumber.name}
              label={attributes.commodityNumber.label}
              type="text"
              required
              onChange={handleOnChange}
              defaultValue={payload.commodityNumber}
            />
            <TextField
              name={attributes.approvalNumber.name}
              label={attributes.approvalNumber.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.approvalNumber}
            />
            <TextField
              name={attributes.referenceNumber.name}
              label={attributes.referenceNumber.label}
              type="text"
              onChange={handleOnChange}
              defaultValue={payload.referenceNumber}
            />
            <DynamicDatePicker
              label={attributes.issueDate.label}
              isRequired={false}
              defaultValue={payload.issueDate}
              name={attributes.issueDate.name}
              handleOnChange={handleOnChange}
            />
          </form>
        ) : (
          <div>
            <DynamicFormCommandBar
              formMode={props.formMode}
              toggleHideDeleteDialog={toggleHideDeleteDialog}
              formName={formName}
              setFormMode={props.setFormMode}
              closeModal={props.closeModal}
            />
            <Label styles={labelStyles}>
              {attributes.commodityNumber.label}
            </Label>
            <span style={valueStyles}>{payload.commodityNumber}</span>
            <Label styles={labelStyles}>
              {attributes.approvalNumber.label}
            </Label>
            <span style={valueStyles}>{payload.approvalNumber}</span>
            <Label styles={labelStyles}>
              {attributes.referenceNumber.label}
            </Label>
            <span style={valueStyles}>{payload.referenceNumber}</span>
            <Label styles={labelStyles}>{attributes.issueDate.label}</Label>
            <span style={valueStyles}>
              {payload.issueDate
                ? moment(payload.issueDate).format("DD.MM.YYYY")
                : ""}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GoodInfoForm;
